import { useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { InvestmentAllocation } from '../../../types';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { groupBy, round, sumBy } from 'lodash';
import AllocationPercentageCell from './AllocationPercentageCell';
import useSyncInvestorAllocations from '../../../hooks/useSyncInvestorAllocations';
import AvatarWithName from 'components/new/AvatarWithName';

type Props = {
  currency: CurrencyCode;
  considerationIndex: number;
  investmentAllocation: Array<InvestmentAllocation>;
  flatInvestmentAllocations: Array<InvestmentAllocation>;
};

type Row = {
  id: string;
  investorName: string;
  allocationsByHoldingClass: Record<string, InvestmentAllocation>;
  receivedTotal: number;
  escrowTotal: number;
  earnoutTotal: number;
  withholdingTotal: number;
};

const CombinedInvestorAllocationsTable: React.FC<Props> = ({
  currency,
  considerationIndex,
  investmentAllocation,
  flatInvestmentAllocations,
}) => {
  const { control } = useFormContext<rules.CreateRealisationFormValues>();
  const { setInvestorAllocations, setInvestorAllocationType } =
    useSyncInvestorAllocations();

  const holdingClasses = Object.values(
    groupBy(investmentAllocation, 'holdingClassId'),
  ).map((allocations) => ({
    holdingClassId: allocations[0].holdingClassId,
    holdingClassName: allocations[0].holdingClassName,
  }));

  const rows: Array<Row> = Object.entries(
    groupBy(investmentAllocation, 'commitmentId'),
  ).map(([commitmentId, allocations]) => ({
    id: commitmentId,
    investorName: allocations[0].investorName,
    allocationsByHoldingClass: Object.fromEntries(
      allocations.map((a) => [a.holdingClassId, a]),
    ),
    receivedTotal: round(sumBy(allocations, 'receivedConsideration'), 2),
    escrowTotal: round(sumBy(allocations, 'escrowConsideration'), 2),
    earnoutTotal: round(sumBy(allocations, 'earnoutConsideration'), 2),
    withholdingTotal: round(sumBy(allocations, 'withholdingTaxPayable'), 2),
  }));

  const grandTotals = {
    received: round(sumBy(rows, 'receivedTotal'), 2),
    escrow: round(sumBy(rows, 'escrowTotal'), 2),
    earnout: round(sumBy(rows, 'earnoutTotal'), 2),
    withholding: round(sumBy(rows, 'withholdingTotal'), 2),
  };

  const totalsByHoldingClass = Object.fromEntries(
    holdingClasses.map(({ holdingClassId }) => {
      const allocations = investmentAllocation.filter(
        (a) => a.holdingClassId === holdingClassId,
      );
      return [
        holdingClassId,
        {
          received: round(sumBy(allocations, 'receivedConsideration'), 2),
          escrow: round(sumBy(allocations, 'escrowConsideration'), 2),
          earnout: round(sumBy(allocations, 'earnoutConsideration'), 2),
          withholding: round(sumBy(allocations, 'withholdingTaxPayable'), 2),
        },
      ];
    }),
  );

  const holdingClassColumns: Array<TableColumn<Row>> = holdingClasses.flatMap(
    ({ holdingClassId, holdingClassName }) => [
      {
        key: `${holdingClassId}_pct`,
        label: `${holdingClassName} %`,
        width: 150,
        render: (row) => {
          const allocation = row.allocationsByHoldingClass[holdingClassId];
          if (!allocation) return null;
          const allocationIndex = flatInvestmentAllocations.findIndex(
            (a) =>
              a.investmentId === allocation.investmentId &&
              a.holdingClassId === holdingClassId &&
              a.commitmentId === row.id,
          );
          if (allocationIndex === -1) return null;
          return (
            <AllocationPercentageCell
              setInvestorAllocations={() =>
                setInvestorAllocations(considerationIndex)
              }
              considerationIndex={considerationIndex}
              setInvestorAllocationType={setInvestorAllocationType}
              investmentId={allocation.investmentId}
              allocationIndex={allocationIndex}
              control={control}
            />
          );
        },
      },
      {
        key: `${holdingClassId}_received`,
        label: 'Received consideration',
        width: 180,
        render: (row) =>
          numberToCurrencyString(
            row.allocationsByHoldingClass[holdingClassId]
              ?.receivedConsideration ?? 0,
            { currency },
          ),
        footer: numberToCurrencyString(
          totalsByHoldingClass[holdingClassId].received,
          { currency },
        ),
      },
      {
        key: `${holdingClassId}_escrow`,
        label: 'Escrow consideration',
        width: 170,
        render: (row) =>
          numberToCurrencyString(
            row.allocationsByHoldingClass[holdingClassId]
              ?.escrowConsideration ?? 0,
            { currency },
          ),
        footer: numberToCurrencyString(
          totalsByHoldingClass[holdingClassId].escrow,
          { currency },
        ),
      },
      {
        key: `${holdingClassId}_earnout`,
        label: 'Earnout consideration',
        width: 170,
        render: (row) =>
          numberToCurrencyString(
            row.allocationsByHoldingClass[holdingClassId]
              ?.earnoutConsideration ?? 0,
            { currency },
          ),
        footer: numberToCurrencyString(
          totalsByHoldingClass[holdingClassId].earnout,
          { currency },
        ),
      },
      {
        key: `${holdingClassId}_withholding`,
        label: 'Withholding tax',
        width: 130,
        render: (row) =>
          numberToCurrencyString(
            row.allocationsByHoldingClass[holdingClassId]
              ?.withholdingTaxPayable ?? 0,
            { currency },
          ),
        footer: numberToCurrencyString(
          totalsByHoldingClass[holdingClassId].withholding,
          { currency },
        ),
      },
    ],
  );

  const columns: Array<TableColumn<Row>> = [
    {
      key: 'investorName',
      label: 'Investor',
      render: ({ investorName }) => (
        <AvatarWithName avatarSize="lg" name={investorName} />
      ),
      footer: `Total ${
        investmentAllocation[0]?.investmentDate?.toReadable() ?? ''
      }`,
      sticky: true,
      withVerticalBorder: true,
      left: 0,
      width: 250,
    },
    ...holdingClassColumns,
    {
      key: 'receivedTotal',
      label: 'Total received consideration',
      width: 210,
      render: ({ receivedTotal }) =>
        numberToCurrencyString(receivedTotal, { currency }),
      footer: numberToCurrencyString(grandTotals.received, { currency }),
      withVerticalBorder: true,
    },
    {
      key: 'escrowTotal',
      label: 'Total escrow consideration',
      width: 210,
      render: ({ escrowTotal }) =>
        numberToCurrencyString(escrowTotal, { currency }),
      footer: numberToCurrencyString(grandTotals.escrow, { currency }),
    },
    {
      key: 'earnoutTotal',
      label: 'Total earnout consideration',
      width: 210,
      render: ({ earnoutTotal }) =>
        numberToCurrencyString(earnoutTotal, { currency }),
      footer: numberToCurrencyString(grandTotals.earnout, { currency }),
    },
    {
      key: 'withholdingTotal',
      label: 'Total withholding tax',
      width: 210,
      render: ({ withholdingTotal }) =>
        numberToCurrencyString(withholdingTotal, { currency }),
      footer: numberToCurrencyString(grandTotals.withholding, { currency }),
    },
  ];

  return <Table columns={columns} rows={rows} scrollable />;
};

export default CombinedInvestorAllocationsTable;
